/**
 * useTextCategories.ts — Extracts text category lists from barMitzvah data.
 *
 * Shared by PracticeSelect (dropdown) and PracticeView (cards).
 */
import type { AliyahType, BarMitzvahType, HaftarahType, SentenceGroupType } from 'types/types'
import { computed, type Ref } from 'vue'

import { formattedAliyahForStudent, formattedHaftarahForStudent } from '@/composables/format'

export interface PracticeListItem {
  title: string | null
  props: object
  aliyah: AliyahType | null
  haftarah: HaftarahType | null
  sentenceGroup: SentenceGroupType | null
}

export function createBlankPracticeListItem(): PracticeListItem {
  return {
    aliyah: null,
    haftarah: null,
    props: {},
    sentenceGroup: null,
    title: null,
  }
}

export function useTextCategories(barMitzvah: Ref<BarMitzvahType | null>) {
  const formattedAliyot = computed<Array<PracticeListItem>>(() => {
    const aliyot = barMitzvah.value?.aliyot
    if (!aliyot?.length) {
      return []
    }

    const labeledAliyot = aliyot.map((aliyah: AliyahType) => ({
      ...createBlankPracticeListItem(),
      aliyah,
      title: formattedAliyahForStudent(aliyah),
    }))

    return [...labeledAliyot].sort(
      (a, b) =>
        (a.aliyah?.parashah.transliteratedTitle ?? '').localeCompare(b.aliyah?.parashah.transliteratedTitle ?? '') ||
        (a.aliyah?.rank ?? 0) - (b.aliyah?.rank ?? 0),
    )
  })

  const formattedHaftarot = computed<Array<PracticeListItem>>(() => {
    const haftarah = barMitzvah.value?.haftarah
    const haftarot = haftarah ? [haftarah] : []
    if (!haftarot.length) {
      return []
    }

    return haftarot.map((xHaftarah: HaftarahType) => ({
      ...createBlankPracticeListItem(),
      haftarah: xHaftarah,
      title: formattedHaftarahForStudent(xHaftarah),
    }))
  })

  const formattedSentenceGroups = computed<Array<PracticeListItem>>(() => {
    const sentenceGroups = barMitzvah.value?.sentenceGroups
    if (!sentenceGroups?.length) {
      return []
    }

    const labeledSentenceGroups = sentenceGroups.map((sentenceGroup: SentenceGroupType) => ({
      ...createBlankPracticeListItem(),
      sentenceGroup,
      title: sentenceGroup.reading.description ?? null,
    }))

    return [...labeledSentenceGroups].sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''))
  })

  /** Sentence groups organized by category (sorted by category order). */
  const sentenceGroupCategoryGroups = computed<Array<{ description: string; items: PracticeListItem[] }>>(() => {
    const items = formattedSentenceGroups.value
    if (!items.length) {
      return []
    }

    const byCategory = new Map<string, { description: string; items: PracticeListItem[]; order: number }>()
    for (const item of items) {
      const cat = item.sentenceGroup?.category
      const key = cat?.id ?? '_none'
      if (!byCategory.has(key)) {
        byCategory.set(key, { description: cat?.description ?? "B'rachot / Songs", items: [], order: cat?.order ?? Infinity })
      }
      byCategory.get(key)!.items.push(item)
    }

    return [...byCategory.entries()].toSorted(([, a], [, b]) => a.order - b.order).map(([, group]) => group)
  })

  /** Sentence groups with per-category disabled headers interleaved (for dropdowns). */
  const formattedSentenceGroupsByCategory = computed<Array<PracticeListItem>>(() => {
    const groups = sentenceGroupCategoryGroups.value
    if (!groups.length) {
      return []
    }

    const makeHeader = (title: string): PracticeListItem => ({ ...createBlankPracticeListItem(), props: { disabled: true }, title })

    if (groups.length <= 1) {
      return [makeHeader("B'rachot / Songs"), ...formattedSentenceGroups.value]
    }

    const result: PracticeListItem[] = []
    for (const { description, items } of groups) {
      result.push(makeHeader(description))
      result.push(...items)
    }
    return result
  })

  return {
    formattedAliyot,
    formattedHaftarot,
    formattedSentenceGroups,
    formattedSentenceGroupsByCategory,
    sentenceGroupCategoryGroups,
  }
}
